/** Shared formatting helpers. */

export const num = (n: number): string => n.toLocaleString('en-US');

/** "Jul 30" — the demo's row-date format. */
export function shortDate(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** "Thu, 30 Jul 2026" — the header's date chip. */
export function headerDate(d = new Date()): string {
  const parts = d.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  // en-GB gives "Thu 30 Jul 2026"; the design puts a comma after the weekday.
  return parts.replace(/^(\w+)\s/, '$1, ');
}

export function fullDate(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * A date-only value ("1958-07-22") in the reader's own terms.
 *
 * `new Date('1958-07-22')` is parsed as UTC midnight, which renders as the day
 * before anywhere west of Greenwich — a date of birth that is off by one is
 * worse than none at all, so the parts are handed to the local constructor
 * instead of the string to the parser.
 */
export function plainDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return fullDate(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
}

/** Age in whole years from a date-only value, or null if it is not one. */
export function ageFrom(iso: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const born = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const now = new Date();
  let years = now.getFullYear() - born.getFullYear();
  const months = now.getMonth() - born.getMonth();
  if (months < 0 || (months === 0 && now.getDate() < born.getDate())) years--;
  return years >= 0 && years < 130 ? years : null;
}

export function dateTime(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return `${fullDate(d)}, ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
}

/**
 * A deadline in the lab's own timezone.
 *
 * SLA clocks are defined in New Haven time — pack 03 states every due time as
 * "17:00 ET" — so rendering them in the viewer's local zone is wrong the
 * moment anyone reads the board from elsewhere. A coordinator in Connecticut
 * and a developer in another timezone must see the same deadline, and it must
 * be the one the clock actually runs to. The zone is shown, not implied.
 */
export function siteDateTime(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(d);
  return parts;
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');
}

/** Signed delta with the demo's arrow glyphs. */
export function delta(n: number): string {
  if (n === 0) return '±0';
  return n > 0 ? `↑ ${n}` : `↓ ${Math.abs(n)}`;
}

/** "Sep 28" — the site's calendar day for an instant, for a compact column. */
export function siteDay(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' }).format(d);
}

/** "812 B", "12 KB", "1.4 MB" — one decimal under ten, none above. */
export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v < 10 ? v.toFixed(1).replace(/\.0$/, '') : Math.round(v)} ${units[i]}`;
}
